import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Form, Input, Typography, Select, Checkbox, Button } from 'antd';
// Api
import {
  CREATE_CAMPAIGN_EXAMPLE_FILES,
  UPDATE_CAMPAIGN,
} from 'api/campaignV2/mutations';
import { SEARCH_CAMPAIGN_STORES } from 'api/campaignV2/queries';
// Helpers
import {
  convertEngagementRateToOption,
  getEngagementRateValue,
} from 'helpers/campaign';
import { getSocialCampaignCommonFields } from 'helpers/campaignSocial';
import { getSocialActivity } from 'helpers/cbActivity';
import {
  FOLLOWING_OPTIONS_MAX,
  FOLLOWING_OPTIONS_MIN,
  getLocationsForApi,
  MEDIA_TYPES,
  PRODUCT_TYPES,
  uploadImages,
  getSelectableGenders,
} from 'helpers/campaignEditModal';
import {
  DeliverableV2,
  TimeLineDeliverable,
  isContentTask,
  isDeliverablesChecklistValid,
  isTimelineDeliverablesChecklistValid,
} from 'helpers/deliverables';
import { transformTasksV2ForUpdate } from 'helpers/timelineDeliverables';
// Constants
import { CampaignVersion } from 'constants/global';
// Types
import {
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2,
  GetCampaignsV2_getCampaigns_entities_search,
} from 'api/campaignV2/types/GetCampaignsV2';
import {
  UpdateCampaign,
  UpdateCampaignVariables,
} from 'api/campaignV2/types/UpdateCampaign';
import {
  CampaignExampleFileInput,
  CampaignLocationInput,
  CampaignMediaType,
  CampaignSmPlatform,
  CampaignTaskType,
  CbApplicationQuestionType,
  CreateCampaignDetailsInput,
  CreateCampaignTaskInput,
  SearchCampaignCriteriaInput,
  SortDirection,
  StoreGender,
  UpdateCampaignInput,
  UserRole,
  AudienceAgeRange,
  AudienceEthnicity,
  AudienceGender,
  AudienceInterest,
} from 'api/graphql-global-types';
import {
  CreateCampaignExampleFiles,
  CreateCampaignExampleFilesVariables,
} from 'api/campaignV2/types/CreateCampaignExampleFiles';
import {
  SearchCampaignStores,
  SearchCampaignStoresVariables,
} from 'api/campaignV2/types/SearchCampaignStores';
// Hooks
import { useGetMyEntitlements } from 'hooks';
// Helpers
import { canEditPremiumFilters } from 'helpers/brands';
// Ui
import UploadFilesInput from 'ui/UploadFIlesInput/UploadFilesInput';
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import TalentQuestions from 'pages/CampaignDetails/CampaignDetails/components/TalentQuestions/TalentQuestions';
import DeliverablesChecklist from 'pages/CampaignDetails/CampaignDetails/components/DeliverablesChecklist/DeliverablesChecklist';
import TimelineDeliverables from 'pages/CampaignDetails/CampaignDetails/components/TimelineDeliverables/TimelineDeliverables';
import CampaignDetails from 'components/common/CampaignEditComponents/CampaignDetails/CampaignDetails';
import CompanyDetails from 'components/common/CampaignEditComponents/CompanyDetails/CompanyDetails';
import CampaignCriteria from 'components/common/CampaignEditComponents/CampaignCriteria/CampaignCriteria';
// Style
import styles from './SocialCampaignsEditModal.module.scss';

const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;

export type SocialCampaignFormValues = {
  userRole: UserRole[];
  budget: number;
  direction?: SortDirection | null;
  flatFeePerStore: number;
  genders?: StoreGender[] | null;
  locations?: CampaignLocationInput[] | null;
  maxAge?: number | null;
  minAge?: number | null;
  smPlatforms: CampaignSmPlatform[];
  followingFrom: number | null;
  followingTo: number | null;
  sports: string[];
  avoid?: string | null;
  brandName: string;
  dueDate: any;
  applicationDeadlineDays?: number | null;
  applicationDeadlineEnabled?: boolean;
  encouraged?: string | null;
  exampleFiles?: CampaignExampleFileInput[] | null;
  hashtags?: string | null;
  instructions: string;
  mediaType?: CampaignMediaType[] | null;
  name: string;
  internalName: string;
  physicalProductType?: string | null;
  productName?: string | null;
  productValue?: number | null;
  profileMentions?: string | null;
  promoCodes?: string | null;
  promoUrls?: string | null;
  quantityInstructions?: string | null;
  scriptUrl?: string | null;
  suggestedCaption?: string | null;
  tasks: CreateCampaignTaskInput[];
  website?: string | null;
  usageRightsText: string | null;
  questions: string[];
  hometownLocations?: CampaignLocationInput[] | null;
  excludedLocations?: CampaignLocationInput[] | null;
  engagementRate?: { value: number; label: string } | null;
  searchHashtags?:
    | { label: string; value: string; storeCount: number }[]
    | null;
  audienceGender?: AudienceGender | null;
  audienceAgeRanges?: AudienceAgeRange[] | null;
  audienceEthnicities?: AudienceEthnicity[] | null;
  audienceLocations?: CampaignLocationInput[] | null;
  audienceInterests?: AudienceInterest[] | null;
};

type ExampleFiles = {
  url: string | null;
  key: string;
  file: File | undefined;
};

type CampaignEditModalProps = {
  record: GetCampaignsV2_getCampaigns_entities;
  onFinish: () => void;
};

const normFile = (e: any) => {
  // AntD expects an array of files
  if (Array.isArray(e)) {
    return e;
  }
  return e && e.fileList;
};

const SocialCampaignsEditModal = ({
  record,
  onFinish,
}: CampaignEditModalProps): JSX.Element | null => {
  const [form] = Form.useForm();

  const ownerId = record?.agency?.id;
  const { data: entitlementsData } = useGetMyEntitlements({
    ownerId,
    skip: !ownerId,
  });
  const canEditPremiumFiltersValue = canEditPremiumFilters(
    entitlementsData?.getMyEntitlements
  );

  const socialActivity = getSocialActivity(record);
  const isCampaignV2 = record.v === CampaignVersion.V2;
  const isCampaignV3 = record.v === CampaignVersion.V3;

  const questions = record?.questions
    ?.filter((item) => item.type === CbApplicationQuestionType.Regular)
    ?.sort((a, b) => a.order - b.order);

  const [selectedTasks, setSelectedTasks] = useState<CampaignTaskType[]>(
    socialActivity?.tasks?.map((item) => item.type) ?? []
  );

  const [deliverablesChecklist, setDeliverablesChecklist] = useState<
    DeliverableV2[]
  >(isCampaignV2 ? socialActivity?.tasks ?? [] : []);

  const [timelineDeliverablesChecklist, setTimelineDeliverablesChecklist] =
    useState<TimeLineDeliverable[]>(
      isCampaignV3
        ? (socialActivity?.tasksV2 ?? [])
            .filter((task) => task.type !== CampaignTaskType.FINAL_CONTENT)
            .map((task: any) => ({
              ...task,
              contentItems: (task.contentItems ?? []).filter(
                (item: any) => item.type !== CampaignTaskType.FINAL_CONTENT
              ),
            }))
        : []
    );

  let validationError: string | null = null;
  if (isCampaignV2) {
    validationError =
      !isDeliverablesChecklistValid(deliverablesChecklist) &&
      (deliverablesChecklist.length > 0 || deliverablesChecklist.length === 0)
        ? 'You must add at least one Content and one Post deliverable.'
        : null;
  }
  if (isCampaignV3) {
    validationError =
      !isTimelineDeliverablesChecklistValid(timelineDeliverablesChecklist) &&
      (timelineDeliverablesChecklist.length > 0 ||
        timelineDeliverablesChecklist.length === 0)
        ? 'You must add at least one Content and one Post deliverable.'
        : null;
  }

  const [isInstagram, setIsInstagram] = useState<boolean>(
    record?.search?.smPlatforms?.includes(CampaignSmPlatform.IG) || false
  );

  const [isTwitter, setIsTwitter] = useState<boolean>(
    record?.search?.smPlatforms?.includes(CampaignSmPlatform.TW) || false
  );

  const [isTikTok, setIsTikTok] = useState<boolean>(
    record?.search?.smPlatforms?.includes(CampaignSmPlatform.TT) || false
  );

  const [isPhysicalProduct, setIsPhysicalProduct] = useState<boolean>(
    socialActivity?.physicalProduct || false
  );

  const [usageRights, setUsageRights] = useState<boolean>(
    record?.usageRights || false
  );

  const [isUploading, setIsUploading] = useState<boolean>(false);

  const filters = record?.search as GetCampaignsV2_getCampaigns_entities_search;

  const [searchStores, { data: storeData, loading: loadingStoreData }] =
    useLazyQuery<SearchCampaignStores, SearchCampaignStoresVariables>(
      SEARCH_CAMPAIGN_STORES
    );

  const [updateCampaign, { loading: loadingUpdateCampaign }] = useMutation<
    UpdateCampaign,
    UpdateCampaignVariables
  >(UPDATE_CAMPAIGN);

  const [
    createCampaignExampleFiles,
    { loading: loadingCreateCampaignExampleFiles },
  ] = useMutation<
    CreateCampaignExampleFiles,
    CreateCampaignExampleFilesVariables
  >(CREATE_CAMPAIGN_EXAMPLE_FILES);

  const initialHashtags = socialActivity?.hashtags.join(',');
  const initialPromoCodes = socialActivity?.promoCodes?.join(',');
  const initialPromoURL = socialActivity?.promoUrls?.join(',');

  const initialFormValues: SocialCampaignFormValues = {
    userRole: filters?.roles || [],
    budget: filters?.budget || 0,
    flatFeePerStore: record?.flatFeePerStore || 0,
    genders: getSelectableGenders(filters?.genders),
    maxAge: filters?.athleteMaxAge || null,
    minAge: filters?.athleteMinAge || null,
    smPlatforms: filters?.smPlatforms || [],
    followingFrom:
      FOLLOWING_OPTIONS_MIN.find((f) => f.value === filters.minFollowers)
        ?.value || null,
    followingTo:
      FOLLOWING_OPTIONS_MAX.find((f) => f.value === filters.maxFollowers)
        ?.value || null,
    sports: filters?.sports || [],
    avoid: socialActivity?.avoid,
    brandName: socialActivity?.brandName || '',
    dueDate: record?.dueDate ? moment(record?.dueDate) : null,
    applicationDeadlineDays: record?.applicationDeadlineDays || null,
    applicationDeadlineEnabled: record?.applicationDeadlineEnabled ?? true,
    encouraged: socialActivity?.encouraged,
    exampleFiles: socialActivity?.files || null,
    hashtags: initialHashtags ?? null,
    instructions: socialActivity?.instructions || '',
    mediaType: socialActivity?.mediaType || null,
    name: record?.name || '',
    internalName: record?.internalName || '',
    physicalProductType: socialActivity?.physicalProductType || null,
    productName: socialActivity?.productName || null,
    productValue: socialActivity?.productValue || null,
    profileMentions: socialActivity?.profileMentions || null,
    promoCodes: initialPromoCodes ?? null,
    promoUrls: initialPromoURL ?? null,
    quantityInstructions: socialActivity?.quantityInstructions || null,
    scriptUrl: socialActivity?.script || null,
    suggestedCaption: socialActivity?.suggestedCaption || null,
    tasks: socialActivity?.tasks,
    website: socialActivity?.website || null,
    usageRightsText: record?.usageRightsText || null,
    questions: questions?.map((item) => item.question),
    locations: record?.search?.locations || [],
    hometownLocations: record?.search?.hometownLocations || [],
    excludedLocations: record?.search?.excludedLocations || [],
    engagementRate: convertEngagementRateToOption(
      record?.search?.engagementRateFrom
    ),
    searchHashtags: record?.search?.hashtags
      ? record.search.hashtags.map((tag) => ({
          label: tag,
          value: tag,
          storeCount: 0,
        }))
      : [],
    audienceGender: filters?.audienceGender || null,
    audienceAgeRanges: filters?.audienceAgeRanges || [],
    audienceEthnicities: filters?.audienceEthnicities || [],
    audienceLocations: filters?.audienceLocations || null,
    audienceInterests: filters?.audienceInterests || [],
  };

  // Set initial form values when record changes (only reset when editing a different campaign)
  useEffect(() => {
    if (record && filters) {
      form.setFieldsValue({
        name: record?.name || '',
        internalName: record?.internalName || '',
        brandName: socialActivity?.brandName || '',
        dueDate: record?.dueDate ? moment(record?.dueDate) : null,
        applicationDeadlineDays: record?.applicationDeadlineDays || null,
        applicationDeadlineEnabled: record?.applicationDeadlineEnabled ?? true,
        flatFeePerStore: record?.flatFeePerStore || 0,
        usageRightsText: record?.usageRightsText || null,
        userRole: filters?.roles || [],
        budget: filters?.budget || 0,
        genders: getSelectableGenders(filters?.genders),
        maxAge: filters?.athleteMaxAge || null,
        minAge: filters?.athleteMinAge || null,
        followingFrom:
          FOLLOWING_OPTIONS_MIN.find((f) => f.value === filters?.minFollowers)
            ?.value || null,
        followingTo:
          FOLLOWING_OPTIONS_MAX.find((f) => f.value === filters?.maxFollowers)
            ?.value || null,
        sports: filters?.sports || [],
        locations: record?.search?.locations || [],
        hometownLocations: record?.search?.hometownLocations || [],
        excludedLocations: record?.search?.excludedLocations || [],
        engagementRate: convertEngagementRateToOption(
          record?.search?.engagementRateFrom
        ),
        searchHashtags: record?.search?.hashtags
          ? record.search.hashtags.map((tag) => ({
              label: tag,
              value: tag,
              storeCount: 0,
            }))
          : [],
        // Set audience filters - preserve values even if subscription is lost (fields will be disabled)
        audienceGender: filters?.audienceGender || null,
        audienceAgeRanges: filters?.audienceAgeRanges || [],
        audienceEthnicities: filters?.audienceEthnicities || [],
        audienceLocations: filters?.audienceLocations || null,
        audienceInterests: filters?.audienceInterests || [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id]);

  const handleSubmit = async (shouldValidate = true) => {
    if (shouldValidate && validationError) {
      errorNotification(validationError);
      return;
    }

    const {
      avoid,
      brandName,
      dueDate,
      encouraged,
      hashtags,
      instructions,
      mediaType,
      name,
      physicalProductType,
      productName,
      productValue,
      profileMentions,
      promoCodes,
      promoUrls,
      quantityInstructions,
      scriptUrl,
      suggestedCaption,
      website,
      usageRightsText,
      questions,
      applicationDeadlineDays,
      internalName,
      // applicationDeadlineEnabled,
    } = form.getFieldsValue();

    const exampleFiles = form.getFieldValue('exampleFiles');
    const commonFields = getSocialCampaignCommonFields(
      form.getFieldsValue(),
      isTikTok
    );

    const searchLocations = getLocationsForApi(commonFields.locations || []);
    const searchAudienceLocations = getLocationsForApi(
      commonFields.audienceFilters?.locations || []
    );
    const hometownLocations = form.getFieldValue('hometownLocations');
    const searchHometownLocations = getLocationsForApi(hometownLocations || []);
    const searchExcludedLocations = getLocationsForApi(
      form.getFieldValue('excludedLocations') || []
    );

    const criteria: SearchCampaignCriteriaInput = {
      ...commonFields,
      ...(searchLocations.length && { locations: searchLocations }),
      ...(searchAudienceLocations.length && {
        audienceFilters: {
          ...commonFields.audienceFilters,
          locations: searchAudienceLocations,
        },
      }),
      ...(searchHometownLocations.length && {
        hometownLocations: searchHometownLocations,
      }),
      ...(searchExcludedLocations.length && {
        excludedLocations: searchExcludedLocations,
      }),
      ...(form.getFieldValue('searchHashtags') && {
        hashtagInputs: form
          .getFieldValue('searchHashtags')
          .map((tag: { label: string }) => ({
            name: tag.label.trim(),
          })),
      }),
      smPlatforms: isInstagram
        ? [CampaignSmPlatform.IG]
        : isTwitter
        ? [CampaignSmPlatform.TW]
        : [CampaignSmPlatform.TT],
    };

    const details: CreateCampaignDetailsInput = {
      ...(avoid && { avoid }),
      ...(brandName && { brandName }),
      ...(dueDate && { dueDate: dueDate }),
      ...(encouraged && { encouraged }),
      ...(exampleFiles &&
        exampleFiles?.length > 0 && {
          exampleFiles,
        }),
      ...(hashtags && {
        hashtags:
          hashtags?.split(',')?.map((tag: string) => tag.trim()) || hashtags,
      }),
      ...(questions?.length > 0 && {
        ...(questions.length && {
          questions: questions?.map((item: string) => ({
            question: item,
            isRequired: true,
          })),
        }),
      }),
      ...(instructions && { instructions }),
      ...(usageRights && { usageRights }),
      ...(usageRightsText && { usageRightsText }),
      ...(!isCampaignV2 &&
        !isCampaignV3 && {
          mediaType: isInstagram || isTwitter ? mediaType : null,
          ...(quantityInstructions && { quantityInstructions }),
        }),
      ...(name && { name }),
      internalName: internalName || name,
      physicalProduct: isPhysicalProduct,
      ...(isPhysicalProduct && {
        physicalProductType,
        productName,
        productValue: Number(productValue),
      }),
      ...(profileMentions && { profileMentions }),
      ...(promoCodes && {
        promoCodes:
          promoCodes?.split(',')?.map((tag: string) => tag.trim()) ||
          promoCodes,
      }),
      ...(promoUrls && {
        promoUrls:
          promoUrls?.split(',')?.map((tag: string) => tag.trim()) || promoUrls,
      }),
      ...(!isCampaignV3 && scriptUrl && { scriptUrl }),
      ...(!isCampaignV3 && suggestedCaption && { suggestedCaption }),
      ...(!isCampaignV2 &&
        !isCampaignV3 &&
        selectedTasks?.length > 0 && {
          tasks: selectedTasks.map((task) => {
            return { type: task };
          }),
        }),
      ...(isCampaignV2 &&
        deliverablesChecklist?.length > 0 && {
          tasks: deliverablesChecklist.map((task) => {
            return {
              type: task.type,
              ...(isContentTask(task as any) && {
                mediaType: (task as any).mediaType,
              }),
              title: task.title,
              description: task.description,
            };
          }),
        }),
      ...(isCampaignV3 && {
        tasksV2: transformTasksV2ForUpdate(timelineDeliverablesChecklist),
      }),
      ...(website && { website }),
      ...(applicationDeadlineDays && { applicationDeadlineDays }),
      // ...(applicationDeadlineEnabled !== undefined && {
      //   applicationDeadlineEnabled,
      // }),
    };

    const updateCampaignInputVariables: UpdateCampaignInput = {
      id: record?.id || '',
      criteria,
      details,
      lockEnabled: true,
      ...(isCampaignV3 && { finalContentEnabled: true }),
    };

    const filesToUpload: File[] = [];
    const uploadUrls: string[] = [];

    exampleFiles?.forEach((file: ExampleFiles) => {
      if (file.key) {
        uploadUrls.push(file.key);
      } else if (file instanceof File) {
        filesToUpload.push(file);
      }
    });

    try {
      if (filesToUpload.length) {
        const { data: campaignExampleFiles } = await createCampaignExampleFiles(
          {
            variables: {
              input: {
                files: filesToUpload.map((file) => ({
                  name: file.name,
                })),
              },
            },
          }
        );
        if (campaignExampleFiles?.createCampaignExampleFiles?.length) {
          setIsUploading(true);
          const urls = await uploadImages(
            campaignExampleFiles.createCampaignExampleFiles,
            filesToUpload
          );
          setIsUploading(false);
          uploadUrls.push(...urls);
        }
      }

      updateCampaignInputVariables.details = {
        ...(updateCampaignInputVariables.details ?? {}),
        exampleFiles: uploadUrls.map((url) => ({ key: url })),
      };
      const { data } = await updateCampaign({
        variables: {
          input: updateCampaignInputVariables,
        },
      });
      if (data?.updateCampaign) {
        successNotification('The campaign was successfully updated');
        onFinish();
      }
    } catch (error) {
      errorNotification((error as Error)?.message);
      console.error('Campaign update error:', { error });
    }
  };

  const handleChange = (taskType: CampaignTaskType) => {
    if (selectedTasks.includes(taskType)) {
      // Remove the task from the selectedTasks array
      setSelectedTasks(selectedTasks.filter((t) => t !== taskType));
    } else {
      // Add the task to the selectedTasks array
      setSelectedTasks([...selectedTasks, taskType]);
    }
  };

  const handleIsPhysicalProductChange = () => {
    if (isPhysicalProduct) {
      form.setFieldsValue({
        physicalProductType: null,
      });
    }

    setIsPhysicalProduct(!isPhysicalProduct);
  };

  const handleUsageRightsChange = () => {
    if (usageRights) {
      form.setFieldsValue({
        usageRights: null,
      });
    }

    setUsageRights(!usageRights);
  };

  const handleFilterSearch = async (criteria: SearchCampaignCriteriaInput) => {
    try {
      searchStores({
        variables: {
          input: {
            limit: 4,
            offset: 0,
            criteria,
          },
        },
      });
    } catch (error) {
      errorNotification((error as Error)?.message);
      console.error('Campaign filter error:', { error });
    }
  };

  const handleFilterClick = async () => {
    const formValues = form.getFieldsValue();

    const commonFields = getSocialCampaignCommonFields(formValues, isTikTok);
    const searchLocations = getLocationsForApi(commonFields.locations || []);
    const searchAudienceLocations = getLocationsForApi(
      commonFields.audienceFilters?.locations || []
    );

    const searchHashtags = form.getFieldValue('searchHashtags');
    const engagementRate = form.getFieldValue('engagementRate');
    const hometownLocations = form.getFieldValue('hometownLocations');
    const searchHometownLocations = getLocationsForApi(hometownLocations || []);
    const searchExcludedLocations = getLocationsForApi(
      form.getFieldValue('excludedLocations') || []
    );

    const criteria: SearchCampaignCriteriaInput = {
      ...commonFields,
      ...(searchLocations.length && { locations: searchLocations }),
      // Add audience locations to criteria if they exist
      ...(searchAudienceLocations.length && {
        audienceFilters: {
          ...commonFields.audienceFilters,
          locations: searchAudienceLocations,
        },
      }),
      ...(searchHometownLocations.length && {
        hometownLocations: searchHometownLocations,
      }),
      ...(searchExcludedLocations.length && {
        excludedLocations: searchExcludedLocations,
      }),
      ...(() => {
        const engagementRateValue = getEngagementRateValue(engagementRate);
        return engagementRateValue !== null
          ? {
              engagementRateRange: {
                from: engagementRateValue,
                to: 1,
              },
            }
          : {};
      })(),
      ...(searchHashtags && {
        hashtagInputs: searchHashtags.map((tag: { label: string }) => ({
          name: tag.label.trim(),
        })),
      }),
      smPlatforms: [
        isInstagram
          ? CampaignSmPlatform.IG
          : isTwitter
          ? CampaignSmPlatform.TW
          : CampaignSmPlatform.TT,
      ],
    };

    handleFilterSearch(criteria);
  };

  const talentsData = storeData?.searchCampaignStores?.entities;

  const hasMore =
    (talentsData?.length ?? 0) < (storeData?.searchCampaignStores.total ?? 0);

  const showTalentList =
    !!talentsData?.length && talentsData?.length > 0 && !loadingStoreData;

  const totalFollowers = Number(
    storeData?.searchCampaignStores.summary.followers
  );
  const totalTalents = Number(storeData?.searchCampaignStores.total);

  useEffect(() => {
    const commonFields = getSocialCampaignCommonFields(initialFormValues);
    const searchLocations = getLocationsForApi(commonFields.locations || []);
    const searchAudienceLocations = getLocationsForApi(
      commonFields.audienceFilters?.locations || []
    );
    const searchHometownLocations = getLocationsForApi(
      initialFormValues.hometownLocations || []
    );
    const searchExcludedLocations = getLocationsForApi(
      initialFormValues.excludedLocations || []
    );

    const criteria: SearchCampaignCriteriaInput = {
      ...commonFields,
      ...(searchLocations.length && { locations: searchLocations }),
      // Add audience locations to criteria if they exist
      ...(searchAudienceLocations.length && {
        audienceFilters: {
          ...commonFields.audienceFilters,
          locations: searchAudienceLocations,
        },
      }),
      ...(searchHometownLocations.length && {
        hometownLocations: searchHometownLocations,
      }),
      ...(searchExcludedLocations.length && {
        excludedLocations: searchExcludedLocations,
      }),
      smPlatforms: initialFormValues.smPlatforms,
    };

    handleFilterSearch(criteria);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitLoading =
    isUploading ||
    loadingUpdateCampaign ||
    loadingCreateCampaignExampleFiles ||
    loadingStoreData;

  if (!record) return null;

  return (
    <div className={styles.root}>
      <Form
        name="EditCampaignForm"
        aria-label="Update Social Campaign"
        layout="vertical"
        onFinish={handleSubmit}
        form={form}
        initialValues={initialFormValues}
      >
        <div className={styles.columnWrapper}>
          <CampaignCriteria
            form={form}
            initialFormValues={initialFormValues}
            record={record}
            isInstagram={isInstagram}
            setIsInstagram={setIsInstagram}
            isTwitter={isTwitter}
            setIsTwitter={setIsTwitter}
            isTikTok={isTikTok}
            setIsTikTok={setIsTikTok}
            setSelectedTasks={setSelectedTasks}
            deliverablesChecklist={
              isCampaignV3
                ? timelineDeliverablesChecklist
                : deliverablesChecklist
            }
            setDeliverablesChecklist={
              isCampaignV3
                ? setTimelineDeliverablesChecklist
                : setDeliverablesChecklist
            }
            handleFilterClick={handleFilterClick}
            loadingStoreData={loadingStoreData}
            talentsData={talentsData}
            hasMore={hasMore}
            totalTalents={totalTalents}
            totalFollowers={totalFollowers}
            showTalentList={showTalentList}
            canEditPremiumFilters={canEditPremiumFiltersValue}
          />

          <CampaignDetails record={record} />

          <CompanyDetails
            renderCustomFields={() => (
              <>
                <div className={styles.rowWrapper}>
                  <Form.Item
                    name="physicalProduct"
                    label={<Text>Physical Products</Text>}
                    valuePropName="checked"
                    initialValue={isPhysicalProduct}
                  >
                    <Checkbox
                      value={isPhysicalProduct}
                      onChange={handleIsPhysicalProductChange}
                    />
                  </Form.Item>
                  <Form.Item
                    name="physicalProductType"
                    rules={[
                      {
                        required: isPhysicalProduct,
                        message: 'This field is required',
                      },
                    ]}
                    label={<Text>Product Shipping Category</Text>}
                  >
                    <Select
                      id="physicalProductType"
                      placeholder="Select"
                      disabled={!isPhysicalProduct}
                      filterOption={(inputValue, option) =>
                        option?.props.children
                          .toString()
                          .toLowerCase()
                          .includes(inputValue.toLowerCase())
                      }
                    >
                      {PRODUCT_TYPES.map((item) => {
                        return (
                          <Option value={`${item.value}`} key={item.label}>
                            {item.label}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </div>

                {isPhysicalProduct && (
                  <div className={styles.rowWrapper}>
                    <Form.Item
                      name="productName"
                      rules={
                        isPhysicalProduct
                          ? [
                              {
                                required: true,
                                message: 'This field is required',
                              },
                            ]
                          : []
                      }
                      label={<Text>Product Name</Text>}
                      initialValue={socialActivity?.productName}
                    >
                      <Input
                        type="text"
                        placeholder="Enter Product Name"
                        aria-label="Product Name"
                      />
                    </Form.Item>

                    <Form.Item
                      name="productValue"
                      rules={
                        isPhysicalProduct
                          ? [
                              {
                                required: true,
                                message: 'This field is required',
                              },
                            ]
                          : []
                      }
                      label={<Text>Product Value</Text>}
                      initialValue={socialActivity?.productValue}
                    >
                      <Input
                        type="number"
                        placeholder="Enter Product Value"
                        aria-label="Product Value"
                      />
                    </Form.Item>
                  </div>
                )}

                <div className={styles.rowWrapper}>
                  <Form.Item
                    name="usageRights"
                    label={<Text>Usage Rights</Text>}
                    valuePropName="checked"
                    initialValue={usageRights}
                  >
                    <Checkbox
                      value={usageRights}
                      onChange={handleUsageRightsChange}
                    />
                  </Form.Item>

                  <Form.Item
                    name="usageRightsText"
                    rules={[
                      {
                        required: usageRights,
                        message: 'This field is required',
                      },
                    ]}
                    label={<Text>Usage rights text</Text>}
                  >
                    <TextArea
                      rows={6}
                      id="usageRights"
                      placeholder="Usage rights text"
                      aria-label="Usage rights text"
                    />
                  </Form.Item>
                </div>
              </>
            )}
          />

          <div className={styles.heading}>Talent Questions:</div>

          <TalentQuestions
            name="questions"
            placeholder="Type a question…"
            editable
            maxQuestions={5}
          />

          <div className={styles.heading}>
            {isCampaignV2 || isCampaignV3
              ? 'Deliverables Checklist:'
              : 'Post Details:'}
          </div>

          <div className={styles.rowWrapper}>
            {isCampaignV2 ? (
              <DeliverablesChecklist
                deliverablesChecklist={deliverablesChecklist}
                setDeliverablesChecklist={setDeliverablesChecklist}
                platform={
                  isInstagram
                    ? CampaignSmPlatform.IG
                    : isTwitter
                    ? CampaignSmPlatform.TW
                    : CampaignSmPlatform.TT
                }
                error={validationError}
              />
            ) : isCampaignV3 ? (
              <TimelineDeliverables
                deliverablesChecklist={
                  timelineDeliverablesChecklist as GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2[]
                }
                setDeliverablesChecklist={
                  setTimelineDeliverablesChecklist as (
                    updater: (
                      prev: GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2[]
                    ) => GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2[]
                  ) => void
                }
                platform={
                  isInstagram
                    ? CampaignSmPlatform.IG
                    : isTwitter
                    ? CampaignSmPlatform.TW
                    : CampaignSmPlatform.TT
                }
                error={validationError}
              />
            ) : (
              <>
                <Form.Item
                  name="profileMentions"
                  label={<Text>Profiles to tag</Text>}
                >
                  <Input
                    type="text"
                    placeholder="Put the usernames of the accounts you want tagged"
                    aria-label="Put the usernames of the accounts you want tagged"
                  />
                </Form.Item>

                {(isInstagram || isTwitter) && (
                  <Form.Item
                    name="mediaType"
                    rules={[
                      {
                        required: true,
                        message: 'This field is required',
                      },
                    ]}
                    label={<Text>Type Of Media</Text>}
                  >
                    <Select
                      id="mediaType"
                      placeholder="Select"
                      mode="multiple"
                      filterOption={(inputValue, option) =>
                        option?.props.children
                          .toString()
                          .toLowerCase()
                          .includes(inputValue.toLowerCase())
                      }
                    >
                      {MEDIA_TYPES.map((item) => {
                        return (
                          <Option value={`${item.value}`} key={item.label}>
                            {item.label}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                )}
              </>
            )}
          </div>

          <div className={styles.rowWrapper}>
            <Form.Item
              name="promoCodes"
              label={<Text>Promo Code (Optional)</Text>}
            >
              <Input
                type="text"
                placeholder="Promo codes for talents to use"
                aria-label="Promo codes for talents to use"
              />
            </Form.Item>

            <Form.Item
              name="promoUrls"
              label={<Text>Promo URL (Optional)</Text>}
            >
              <Input
                type="text"
                placeholder="Link for talents to use"
                aria-label="Link for talents to use"
              />
            </Form.Item>
          </div>

          {!isCampaignV2 && !isCampaignV3 && isInstagram && (
            <div className={styles.instagramPost}>
              <Text>Post Type:</Text>
              <div className={styles.columnWrapper}>
                <div className={styles.instagramTypesWrapper}>
                  <Checkbox
                    checked={selectedTasks.includes(CampaignTaskType.IG_POST)}
                    onChange={() => handleChange(CampaignTaskType.IG_POST)}
                  >
                    Post
                  </Checkbox>
                  <Checkbox
                    checked={selectedTasks.includes(CampaignTaskType.IG_STORY)}
                    onChange={() => handleChange(CampaignTaskType.IG_STORY)}
                  >
                    Story
                  </Checkbox>
                  <Checkbox
                    checked={selectedTasks.includes(CampaignTaskType.IG_REEL)}
                    onChange={() => handleChange(CampaignTaskType.IG_REEL)}
                  >
                    Reel
                  </Checkbox>
                  <Checkbox
                    checked={selectedTasks.includes(CampaignTaskType.IG_LIVE)}
                    onChange={() => handleChange(CampaignTaskType.IG_LIVE)}
                  >
                    Live
                  </Checkbox>
                </div>

                <Form.Item
                  name="quantityInstructions"
                  rules={[
                    {
                      required: true,
                      message: 'This field is required',
                    },
                  ]}
                  label={
                    <Text>
                      Post quantity (How many per item selected (Posts / Reels /
                      Stories)?)
                    </Text>
                  }
                >
                  <Input
                    type="quantityInstructions"
                    placeholder="For example: 1 Reel, 1 Story"
                    aria-label="For example: 1 Reel, 1 Story"
                  />
                </Form.Item>
              </div>
            </div>
          )}

          {!isCampaignV2 && !isCampaignV3 && isTwitter && (
            <div className={styles.instagramPost}>
              <Text>Post Type:</Text>
              <div className={styles.columnWrapper}>
                <div className={styles.instagramTypesWrapper}>
                  <Checkbox
                    checked={selectedTasks.includes(CampaignTaskType.TW_TWEET)}
                    onChange={() => handleChange(CampaignTaskType.TW_TWEET)}
                  >
                    Tweet
                  </Checkbox>
                  <Checkbox
                    checked={selectedTasks.includes(CampaignTaskType.TW_VIDEO)}
                    onChange={() => handleChange(CampaignTaskType.TW_VIDEO)}
                  >
                    Video
                  </Checkbox>
                  <Checkbox
                    checked={selectedTasks.includes(CampaignTaskType.TW_LIVE)}
                    onChange={() => handleChange(CampaignTaskType.TW_LIVE)}
                  >
                    Live
                  </Checkbox>
                </div>

                <Form.Item
                  name="quantityInstructions"
                  rules={[
                    {
                      required: true,
                      message: 'This field is required',
                    },
                  ]}
                  label={
                    <Text>
                      Post quantity (How many per item selected (Tweets / Videos
                      / Lives)?)
                    </Text>
                  }
                >
                  <Input
                    type="quantityInstructions"
                    placeholder="For example: 1 Tweet, 1 Video"
                    aria-label="For example: 1 Tweet, 1 Video"
                  />
                </Form.Item>
              </div>
            </div>
          )}

          <div className={styles.rowWrapper}>
            <Form.Item name="hashtags" label="Hashtags To Use">
              <Input
                type="hashtags"
                placeholder="Enter hashtags to use"
                aria-label="hashtags"
              />
            </Form.Item>
          </div>

          {!isCampaignV3 && (
            <div className={styles.columnWrapper}>
              <Text>
                Suggested Post Caption (We recommend that you suggest captions
                but let talent have creative freedom to edit to match their
                personal voice):
              </Text>
              <Form.Item name="suggestedCaption">
                <TextArea
                  autoSize={{ minRows: 3 }}
                  name="suggestedCaption"
                  placeholder="Enter instructions"
                />
              </Form.Item>
            </div>
          )}

          <div className={styles.columnWrapper}>
            <Text>I would like the talent to do:</Text>
            <Form.Item name="encouraged">
              <TextArea
                autoSize={{ minRows: 3 }}
                name="encouraged"
                placeholder="Provide suggestions, encouragement, or instructions for the talent on what you'd like them to DO."
              />
            </Form.Item>
          </div>

          <div className={styles.columnWrapper}>
            <Text>I don&apos;t want the talent to do:</Text>
            <Form.Item name="avoid">
              <TextArea
                autoSize={{ minRows: 3 }}
                name="avoid"
                placeholder="Provide instructions for the talent on what you DON'T want them to do."
              />
            </Form.Item>
          </div>

          {!isCampaignV3 && (
            <div className={styles.columnWrapper}>
              <Text>Script (Optional):</Text>
              <Form.Item name="scriptUrl">
                <TextArea
                  autoSize={{ minRows: 3 }}
                  name="scriptUrl"
                  placeholder="Suggest what you'd like them to say"
                />
              </Form.Item>
            </div>
          )}
          <div className={styles.rowWrapper}>
            <Form.Item getValueFromEvent={normFile} shouldUpdate>
              {(form) => {
                return (
                  <UploadFilesInput
                    title="Upload Example Files"
                    value={form.getFieldValue('exampleFiles') || []}
                    onChange={(newValue) =>
                      form.setFieldsValue({ exampleFiles: newValue })
                    }
                    acceptFileTypes={[
                      'image/png',
                      'image/jpeg',
                      'application/pdf',
                      'video/mp4',
                      'video/mov',
                      'video/avi',
                      'video/wmv',
                      'video/flv',
                      'video/mpeg',
                      'video/mpg',
                    ]}
                  />
                );
              }}
            </Form.Item>
          </div>
        </div>

        <div className={styles.submitButtonWrapper}>
          <Button
            style={{ marginRight: 8 }}
            type="primary"
            loading={submitLoading}
            onClick={() => handleSubmit(false)}
          >
            Save as Draft
          </Button>

          <Button
            type="primary"
            htmlType="submit"
            loading={submitLoading}
            disabled={!form.isFieldsValidating || submitLoading}
          >
            Submit
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default SocialCampaignsEditModal;
